import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { roomBookingsApi, roomsApi } from '@/api/endpoints'
import type { RoomBookingKind } from '@/api/types'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { SelectField, TextField } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { TimeSelect } from '@/components/ui/TimeSelect'
import { useToast } from '@/components/ui/Toast'
import { RODZAJE_REZERWACJI } from '@/lib/roomBookings'
import { combineToIso, toIsoDate } from '@/lib/scheduleTime'

/**
 * Czasy trwania inne niż przy zajęciach.
 *
 * Remont czy nagranie liczy się w godzinach, nie w lekcjach — dlatego lista
 * zaczyna się tam, gdzie lekcyjna się kończy, i sięga całego dnia.
 */
const CZASY = [
  { value: '30', label: '30 minut' },
  { value: '60', label: '1 godzina' },
  { value: '120', label: '2 godziny' },
  { value: '240', label: '4 godziny' },
  { value: '480', label: '8 godzin' },
  { value: '720', label: 'Pół doby' },
  { value: '1440', label: 'Cały dzień' },
]

interface Props {
  open: boolean
  onClose: () => void
  locationId: string
  /** Wypełnia formularz po kliknięciu w pustą godzinę siatki. */
  preset?: { day: Date; hour: number; roomId?: string } | null
}

interface FormState {
  roomId: string
  date: string
  time: string
  duration: string
  kind: RoomBookingKind
  note: string
}

/**
 * Zajęcie sali na coś, co nie jest lekcją.
 *
 * Osobne okno od zajęć, bo to inna decyzja: nie ma tu ucznia, prowadzącego ani
 * instrumentu, jest za to powód. Wspólny formularz z polami wyszarzanymi w
 * połowie przypadków byłby trudniejszy w obsłudze niż dwa proste.
 */
export function RoomBookingFormModal({ open, onClose, locationId, preset }: Props) {
  const queryClient = useQueryClient()
  const { notifySuccess } = useToast()

  const [form, setForm] = useState<FormState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const roomsQuery = useQuery({
    queryKey: ['rooms', 'picker', locationId],
    queryFn: () => roomsApi.list({ size: 200, location_id: locationId || undefined }),
    enabled: open,
  })
  const rooms = roomsQuery.data?.items ?? []

  useEffect(() => {
    if (!open) return
    setError(null)
    setFieldErrors({})

    const day = preset?.day ?? new Date()
    setForm({
      roomId: preset?.roomId ?? rooms[0]?.id ?? '',
      date: toIsoDate(day),
      time: `${String(preset?.hour ?? 9).padStart(2, '0')}:00`,
      duration: '60',
      kind: 'MEETING',
      note: '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preset, rooms.length])

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((current) => (current ? { ...current, [field]: value } : current))
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error('Formularz nie jest gotowy.')
      return roomBookingsApi.create({
        room_id: form.roomId,
        starts_at: combineToIso(new Date(`${form.date}T00:00:00`), form.time),
        duration_minutes: Number(form.duration),
        kind: form.kind,
        note: form.note.trim() || null,
      })
    },
    onSuccess: async () => {
      // Rezerwacje rysują się w tych samych siatkach co zajęcia, więc odświeżamy
      // oba klucze — inaczej blok pojawiłby się dopiero po przeładowaniu strony.
      await queryClient.invalidateQueries({ queryKey: ['room-bookings'] })
      await queryClient.invalidateQueries({ queryKey: ['schedule'] })
      notifySuccess('Sala została zarezerwowana.')
      onClose()
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiError) {
        setError(caught.message)
        setFieldErrors(caught.fieldErrors)
      } else {
        setError('Nie udało się zarezerwować sali.')
      }
    },
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setFieldErrors({})
    mutation.mutate()
  }

  if (!form) return null

  // Przy powodzie „inne” sama etykieta nic nie mówi — tego samego pilnuje serwer.
  const opisWymagany = form.kind === 'OTHER'
  const canSubmit = form.roomId !== '' && (!opisWymagany || form.note.trim() !== '')

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Rezerwacja sali"
      description="Sala zajęta poza zajęciami — remont, spotkanie, nagranie. W tym czasie nikt nie wpisze tu lekcji."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            form="room-booking-form"
            isLoading={mutation.isPending}
            disabled={!canSubmit}
          >
            Zarezerwuj salę
          </Button>
        </>
      }
    >
      <form id="room-booking-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
        {error && <Alert tone="error">{error}</Alert>}

        <SelectField
          label="Sala"
          value={form.roomId}
          onChange={(event) => update('roomId', event.target.value)}
          options={rooms.map((room) => ({ value: room.id, label: room.name }))}
          placeholder="Wybierz salę"
          error={fieldErrors.room_id}
          required
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            label="Data"
            type="date"
            value={form.date}
            onChange={(event) => update('date', event.target.value)}
            error={fieldErrors.starts_at}
            required
          />
          <TimeSelect
            label="Godzina"
            value={form.time}
            onChange={(value) => update('time', value)}
            required
          />
          <SelectField
            label="Czas trwania"
            value={form.duration}
            onChange={(event) => update('duration', event.target.value)}
            options={CZASY}
            error={fieldErrors.duration_minutes}
            required
          />
        </div>

        <SelectField
          label="Powód"
          value={form.kind}
          onChange={(event) => update('kind', event.target.value as RoomBookingKind)}
          options={RODZAJE_REZERWACJI.map(([value, label]) => ({ value, label }))}
          required
        />

        <TextField
          label="Opis"
          value={form.note}
          onChange={(event) => update('note', event.target.value)}
          placeholder="np. cyklinowanie podłogi, spotkanie z rodzicami"
          hint={
            opisWymagany
              ? 'Przy powodzie „inne” opis jest wymagany — inaczej nikt nie odgadnie, czego dotyczy blokada.'
              : 'Widoczny w grafiku, także dla nauczycieli.'
          }
          error={fieldErrors.note}
          maxLength={255}
          required={opisWymagany}
        />
      </form>
    </Modal>
  )
}
